<template>
  <v-container fluid class="shipment-details">
    <div class="detail-print-action no-print">
      <v-btn class="detail-print-button" @click="imprimirDetalle">
        <v-icon left>mdi-printer-outline</v-icon>
        Imprimir detalle
      </v-btn>
    </div>
    <section class="shipment-summary">
      <div class="summary-heading">
        <div class="section-title">
          <v-icon>mdi-clipboard-text-outline</v-icon>
          <div>
            <p class="section-kicker">Detalle del envío</p>
            <h2>Información de operación</h2>
          </div>
        </div>
        <div class="reference-block">
          <span>Referencia IPS</span>
          <strong>{{ itemi.REFERENCIA }}</strong>
          <p class="header-metadata">
            <v-icon small>mdi-storefront-outline</v-icon>{{ itemi.SUCURSAL }} · Envío registrado por
            {{ itemi.USUARIO }}
          </p>
        </div>
      </div>

      <v-row dense>
        <v-col cols="12">
          <v-sheet class="operation-panel" outlined>
            <div class="panel-title">
              <v-icon>mdi-account-group-outline</v-icon>
              <span>Partes del envío</span>
            </div>
            <v-row dense class="operation-grid">
              <v-col cols="12" :md="esMismoClienteRemitente ? 6 : 4">
                <div class="entity-block">
                  <p class="field-label">
                    {{ esMismoClienteRemitente ? 'Cliente y remitente' : 'Cliente' }}
                  </p>
                  <p class="entity-value">{{ itemi.REMITENTE.nombre }}</p>
                </div>
              </v-col>
              <v-col v-if="!esMismoClienteRemitente" cols="12" md="4">
                <div class="entity-block">
                  <p class="field-label">Remitente</p>
                  <p class="entity-value">{{ itemi.TITULAR || itemi.REMITENTE.nombre }}</p>
                  <p v-if="itemi.REMITENTE.alias" class="entity-detail">
                    {{ itemi.REMITENTE.alias }}
                  </p>
                  <p v-if="itemi.REMITENTE.telefono" class="entity-detail">
                    <v-icon small>mdi-phone-outline</v-icon>{{ itemi.REMITENTE.telefono }}
                  </p>
                </div>
              </v-col>
              <v-col cols="12" :md="esMismoClienteRemitente ? 6 : 4">
                <div class="entity-block">
                  <p class="field-label">Destinatario</p>
                  <p class="entity-value">{{ itemi.DESTINATARIO.nombre }}</p>
                  <p v-if="itemi.DESTINATARIO.alias" class="entity-detail">
                    {{ itemi.DESTINATARIO.alias }}
                  </p>
                  <p v-if="itemi.DESTINATARIO.telefono" class="entity-detail">
                    <v-icon small>mdi-phone-outline</v-icon>{{ itemi.DESTINATARIO.telefono }}
                  </p>
                </div>
              </v-col>
            </v-row>
          </v-sheet>
        </v-col>

        <v-col cols="12">
          <v-sheet class="operation-panel" outlined>
            <div class="panel-title">
              <v-icon>mdi-map-marker-path</v-icon>
              <span>Ruta</span>
            </div>
            <v-row dense class="operation-grid">
              <v-col cols="12" :md="itemi.DIRECCION_REMITENTE ? 6 : 12">
                <div class="route-stop">
                  <p class="field-label"><v-icon small>mdi-map-marker-outline</v-icon>Destino</p>
                  <p class="entity-value">
                    {{ itemi.DIRECCION[itemi.DIRECCION.length - 1].direccion }}
                  </p>
                  <p class="entity-detail">
                    {{ itemi.DIRECCION[itemi.DIRECCION.length - 1].colonia }} ·
                    {{ itemi.DIRECCION[itemi.DIRECCION.length - 1].codigopostal }}
                  </p>
                  <p class="entity-detail">
                    {{ itemi.DIRECCION[itemi.DIRECCION.length - 1].municipio }},
                    {{ itemi.DIRECCION[itemi.DIRECCION.length - 1].estado }}
                  </p>
                  <p class="entity-detail">
                    {{ itemi.DIRECCION[itemi.DIRECCION.length - 1].pais }}
                  </p>
                </div>
              </v-col>
              <v-col v-if="itemi.DIRECCION_REMITENTE" cols="12" md="6">
                <div class="route-stop">
                  <p class="field-label"><v-icon small>mdi-home-map-marker</v-icon>Origen</p>
                  <p class="entity-value">
                    {{ itemi.DIRECCION_REMITENTE[itemi.DIRECCION_REMITENTE.length - 1].direccion }}
                  </p>
                  <p class="entity-detail">
                    {{ itemi.DIRECCION_REMITENTE[itemi.DIRECCION_REMITENTE.length - 1].colonia }} ·
                    {{
                      itemi.DIRECCION_REMITENTE[itemi.DIRECCION_REMITENTE.length - 1]
                        .codigopostal
                    }}
                  </p>
                  <p class="entity-detail">
                    {{ itemi.DIRECCION_REMITENTE[itemi.DIRECCION_REMITENTE.length - 1].municipio }},
                    {{ itemi.DIRECCION_REMITENTE[itemi.DIRECCION_REMITENTE.length - 1].estado }}
                  </p>
                  <p class="entity-detail">
                    {{ itemi.DIRECCION_REMITENTE[itemi.DIRECCION_REMITENTE.length - 1].pais }}
                  </p>
                </div>
              </v-col>
            </v-row>
          </v-sheet>
        </v-col>

        <v-col v-if="itemi.SERVICIO_ADICIONAL" cols="12">
          <v-sheet class="operation-panel" outlined>
            <div class="panel-title">
              <v-icon>mdi-package-variant-plus</v-icon>
              <span>Servicios adicionales</span>
            </div>
            <div class="service-list">
              <p v-if="itemi.SERVICIO_ADICIONAL.correccion_direccion">
                Corrección de dirección
                <strong>
                  {{ reductorCorrecionDireccion(itemi.SERVICIO_ADICIONAL.correccion_direccion) }}
                </strong>
              </p>
              <p v-if="itemi.SERVICIO_ADICIONAL.empaque">
                Empaque <strong>{{ formateaMoneda(itemi.SERVICIO_ADICIONAL.empaque) }}</strong>
              </p>
              <p v-if="itemi.SERVICIO_ADICIONAL.rempaque">
                Reempaque <strong>{{ formateaMoneda(itemi.SERVICIO_ADICIONAL.rempaque) }}</strong>
              </p>
              <p v-if="itemi.SERVICIO_ADICIONAL.entrega_sabado">
                Entrega en sábado
                <strong>{{ formateaMoneda(itemi.SERVICIO_ADICIONAL.entrega_sabado) }}</strong>
              </p>
              <p v-if="itemi.SERVICIO_ADICIONAL.recoleccion">
                Recolección
                <strong>{{ formateaMoneda(itemi.SERVICIO_ADICIONAL.recoleccion) }}</strong>
              </p>
              <p v-if="itemi.SERVICIO_ADICIONAL.zona_expedida">
                Zona extendida
                <strong>{{ formateaMoneda(itemi.SERVICIO_ADICIONAL.zona_expedida) }}</strong>
              </p>
              <p v-if="itemi.SERVICIO_ADICIONAL.servicio_guia">
                FLAT <strong>{{ formateaMoneda(itemi.SERVICIO_ADICIONAL.servicio_guia) }}</strong>
              </p>
              <p v-if="itemi.SERVICIO_ADICIONAL.seguro">
                Seguro <strong>{{ formateaMoneda(itemi.SERVICIO_ADICIONAL.seguro) }}</strong>
                <span>
                  Valor declarado:
                  {{ formateaMoneda(itemi.SERVICIO_ADICIONAL.valorcontenido) }}
                </span>
              </p>
            </div>
          </v-sheet>
        </v-col>
      </v-row>
    </section>

    <section v-if="mensajeria.length" class="shipment-section">
      <div class="section-heading">
        <div class="section-title">
          <v-icon>mdi-truck-fast-outline</v-icon>
          <div>
            <p class="section-kicker">Traslado</p>
            <h3>Mensajería</h3>
          </div>
        </div>
        <span class="item-count">{{ mensajeria.length }} registro(s)</span>
      </div>
      <v-row dense>
        <v-col
          v-for="(item4, index4) in mensajeria"
          :key="`mensaje-${index4}`"
          cols="12"
          sm="6"
          lg="4"
        >
          <v-sheet class="shipment-card" outlined>
            <div class="shipment-card-heading">
              <v-icon>mdi-truck-fast-outline</v-icon>
              <span>Mensaje {{ index4 + 1 }}</span>
            </div>
            <dl class="data-list">
              <div><dt>Peso</dt><dd>{{ item4.pesokg }} kg</dd></div>
              <div><dt>Guía</dt><dd>{{ formateaGuia(item4.guia) }}</dd></div>
            </dl>
          </v-sheet>
        </v-col>
      </v-row>
    </section>

    <section v-if="paquetesEnvio.length" class="shipment-section">
      <div class="section-heading">
        <div class="section-title">
          <v-icon>mdi-package-variant-closed</v-icon>
          <div>
            <p class="section-kicker">Contenido</p>
            <h3>Paquetes</h3>
          </div>
        </div>
        <span class="item-count">{{ paquetesEnvio.length }} paquete(s)</span>
      </div>
      <v-row dense>
        <v-col
          v-for="(item3, index3) in paquetesEnvio"
          :key="`paquete-${index3}`"
          cols="12"
          sm="6"
          lg="4"
        >
          <v-sheet class="shipment-card" outlined>
            <div class="shipment-card-heading">
              <v-icon>mdi-package-variant-closed</v-icon>
              <span>Paquete {{ index3 + 1 }}</span>
            </div>
            <dl class="data-list">
              <div>
                <dt>Dimensiones</dt>
                <dd>{{ item3.alto }} x {{ item3.largo }} x {{ item3.ancho }} cm</dd>
              </div>
              <div><dt>Peso real</dt><dd>{{ item3.pesokg }} kg</dd></div>
              <div><dt>Peso volumétrico</dt><dd>{{ item3.pesovol }} kg</dd></div>
              <div><dt>Guía FedEx</dt><dd>{{ formateaGuia(item3.guia) }}</dd></div>
            </dl>
          </v-sheet>
        </v-col>
      </v-row>
    </section>

    <section class="shipment-section payment-section">
      <div class="section-heading">
        <div class="section-title">
          <v-icon>mdi-cash-multiple</v-icon>
          <div>
            <p class="section-kicker">Cobranza</p>
            <h3>Abonos</h3>
          </div>
        </div>
      </div>
      <v-row dense>
        <v-col cols="12" md="4">
          <v-sheet class="payment-summary">
            <div class="payment-summary-heading">
              <v-icon>mdi-cash-multiple</v-icon>
              <span>Resumen de cobranza</span>
            </div>
            <p class="payment-total-label">Total del envío</p>
            <strong class="payment-total">{{ formateaMoneda(itemi.ABONOS.total) }}</strong>
            <div class="payment-details">
              <div class="payment-detail">
                <span>Estatus de pago</span>
                <v-chip class="payment-status" small label>{{ itemi.ESTADO || 'Sin estatus' }}</v-chip>
              </div>
              <div class="payment-detail">
                <span>Tipo de envío</span>
                <b>{{ itemi.DETALLE_ENVIO || 'No especificado' }}</b>
              </div>
            </div>
            <div class="payment-debt">
              <span>Deuda actual</span>
              <b>{{ formateaMoneda(calculaDeudaActual(itemi.ABONOS)) }}</b>
            </div>
          </v-sheet>
        </v-col>
        <v-col
          v-for="(item2, index2) in itemi.ABONOS.Abonos"
          :key="`abono-${index2}`"
          cols="12"
          md="4"
        >
          <v-sheet class="payment-record" outlined>
            <div class="shipment-card-heading">
              <v-icon>mdi-cash-check</v-icon>
              <span>Abono {{ index2 + 1 }}</span>
            </div>
            <dl class="data-list">
              <div><dt>Fecha</dt><dd>{{ item2.fecha }}</dd></div>
              <div><dt>Importe</dt><dd>{{ formateaMoneda(item2.cantidad) }}</dd></div>
              <div><dt>Método</dt><dd>{{ item2.tipo_pago }}</dd></div>
              <div><dt>Registró</dt><dd>{{ item2.usuario.nombre }}</dd></div>
            </dl>
            <v-btn
              class="reprint-button no-print"
              outlined
              block
              @click="reimprimirAbono(item2)"
            >
              <v-icon small left>mdi-printer-outline</v-icon>Reimprimir abono
            </v-btn>
          </v-sheet>
        </v-col>
      </v-row>
    </section>

    <section v-if="esEnvioInternacional" class="shipment-section tariff-section">
      <div class="section-heading">
        <div class="section-title">
          <v-icon>mdi-file-document-outline</v-icon>
          <div>
            <p class="section-kicker">Documentación internacional</p>
            <h3>Fracciones arancelarias</h3>
          </div>
        </div>
      </div>
      <v-row dense>
        <v-col
          v-for="(el, i) in paquetesEnvio"
          :key="`fraccion-${i}`"
          cols="12"
          lg="6"
        >
          <v-sheet class="tariff-panel" outlined>
            <div class="shipment-card-heading">
              <v-icon>mdi-file-document-outline</v-icon>
              <span>Paquete {{ i + 1 }}</span>
            </div>
            <div v-for="(fraccion, j) in el.fa" :key="j" class="tariff-item">
              <strong>{{ fraccion.id }} · {{ fraccion.nombre }}</strong>
              <span>{{ fraccion.num_clasificacion }}</span>
              <p>{{ fraccion.des_en }}</p>
              <p>
                Cantidad: {{ fraccion.cantidad }} - Valor unitario: {{ fraccion.valor_unitario }}
              </p>
            </div>
          </v-sheet>
        </v-col>
      </v-row>
    </section>
  </v-container>
</template>
<script>
import { FormatoMoneda, FormatoGuia } from '@/Utils/Utils';

export default {
  props: {
    itemi: Object,
  },
  computed: {
    paquetesEnvio() {
      const paquetes = this.itemi && this.itemi.PAQUETES;
      return paquetes && Array.isArray(paquetes.InfoPaquetes) ? paquetes.InfoPaquetes : [];
    },
    mensajeria() {
      const paquetes = this.itemi && this.itemi.PAQUETES;
      return paquetes && Array.isArray(paquetes.InfoMensajeria)
        ? paquetes.InfoMensajeria
        : [];
    },
    esMismoClienteRemitente() {
      const cliente = this.itemi.REMITENTE.nombre;
      const remitente = this.itemi.TITULAR || cliente;
      return cliente === remitente;
    },
    esEnvioInternacional() {
      return (
        this.itemi.DETALLE_ENVIO === 'Internacional Priority'
        || this.itemi.DETALLE_ENVIO === 'Internacional Economy'
      );
    },
  },
  methods: {
    formateaMoneda(moneda) {
      return FormatoMoneda.format(moneda);
    },
    reductorCorrecionDireccion(arrCostoDirecciones) {
      return arrCostoDirecciones.reduce(
        (total, costoDireccion) => total + parseFloat(costoDireccion),
        0,
      );
    },
    formateaGuia(guia) {
      return FormatoGuia(guia);
    },
    calculaDeudaActual(abonos) {
      const pagosHechos = abonos.Abonos.reduce(
        (total, abono) => total + parseFloat(abono.cantidad),
        0,
      );
      const deudaActual = parseFloat(abonos.total) - parseFloat(pagosHechos);
      return deudaActual;
    },
    imprimirDetalle() {
      this.$emit('imprimir-detalle');
    },
    reimprimirAbono(abono) {
      this.$emit('reimprimir-abono', {
        fechaAbono: abono.fecha,
        cantidadAbonada: abono.cantidad,
        referencia: this.itemi.REFERENCIA,
        costoTotalEnvio: this.itemi.ABONOS.total,
        deudaActualEnvio: this.calculaDeudaActual(this.itemi.ABONOS),
        abonante: this.itemi.REMITENTE.nombre,
        totalAbonado:
          this.itemi.ABONOS.total - this.calculaDeudaActual(this.itemi.ABONOS),
      });
    },
  },
};
</script>
<style lang="css">
.shipment-details-expanded {
  background: #eaf2f4;
}

.shipment-details {
  background:
    linear-gradient(135deg, rgba(255, 255, 255, 0.62), rgba(218, 234, 231, 0.82)),
    #eaf2f4;
  border: 1px solid #d9e4e0;
  border-radius: 14px;
  color: #183b4b;
  font-family: "Open Sans", sans-serif;
  margin: 20px auto;
  max-width: 1320px;
  padding: 20px !important;
  width: 100%;
}

.detail-print-action {
  display: flex;
  justify-content: center;
  margin-bottom: 16px;
}

.detail-print-button {
  background: #1976d2 !important;
  color: #fff !important;
  font-weight: 800;
}

.detail-print-button .v-icon {
  color: #fff !important;
}

.shipment-summary,
.shipment-section {
  background: #f3f6f5;
  border: 1px solid #d9e4e0;
  border-bottom: 1px solid #d9e4e0;
  border-radius: 10px;
  margin-bottom: 32px;
  overflow: hidden;
  padding: 0 20px 24px;
}

.summary-heading,
.section-heading {
  align-items: flex-end;
  background: #1976d2;
  color: #fff;
  display: flex;
  justify-content: space-between;
  margin: 0 -20px 20px;
  padding: 14px 18px;
}

.section-title {
  align-items: center;
  display: flex;
  gap: 12px;
}

.section-title .v-icon {
  color: #fff;
}

.section-kicker {
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  margin: 0 0 4px;
}

.summary-heading h2,
.section-heading h3 {
  color: #fff;
  font-family: Poppins, "Open Sans", sans-serif;
  font-weight: 900;
  letter-spacing: 0;
  line-height: 1.2;
  margin: 0;
}

.summary-heading h2 {
  font-size: 24px;
}

.section-heading h3 {
  font-size: 18px;
}

.reference-block,
.item-count {
  color: #fff;
  font-size: 12px;
  font-weight: 700;
}

.reference-block {
  text-align: right;
}

.reference-block span,
.reference-block strong {
  display: block;
}

.reference-block strong {
  color: #fff;
  font-size: 14px;
  font-weight: 700;
}

.header-metadata {
  align-items: center;
  display: flex;
  font-size: 11px;
  font-weight: 600;
  gap: 4px;
  justify-content: flex-end;
  margin: 4px 0 0;
}

.header-metadata .v-icon {
  color: #fff;
}

.operation-panel,
.shipment-card,
.payment-record,
.tariff-panel {
  border-color: #d9e4e0 !important;
  border-radius: 10px;
  height: 100%;
  overflow: hidden;
  padding: 18px;
}

.operation-panel:hover,
.shipment-card:hover,
.payment-record:hover,
.tariff-panel:hover {
  border-color: #9fc8c2 !important;
}

.panel-title,
.shipment-card-heading {
  align-items: center;
  background: #1976d2;
  color: #fff;
  display: flex;
  font-size: 13px;
  font-weight: 700;
  gap: 8px;
  margin: -18px -18px 16px;
  padding: 10px 14px;
}

.panel-title .v-icon,
.shipment-card-heading .v-icon,
.entity-detail .v-icon,
.field-label .v-icon {
  color: #fff;
}

.operation-grid {
  margin: 0;
}

.entity-block,
.route-stop {
  height: 100%;
  padding: 6px 12px;
}

.field-label,
.entity-value,
.entity-detail {
  margin: 0;
}

.field-label {
  align-items: center;
  color: #607778;
  display: flex;
  font-size: 12px;
  font-weight: 700;
  gap: 5px;
}

.field-label .v-icon {
  color: #0b7285;
}

.entity-value {
  color: #183b4b;
  font-size: 15px;
  font-weight: 700;
  margin-top: 5px;
}

.entity-detail {
  align-items: center;
  color: #607778;
  display: flex;
  font-size: 13px;
  font-weight: 700;
  gap: 6px;
  margin-top: 5px;
}

.entity-detail .v-icon {
  color: #0b7285;
}

@media (min-width: 960px) {
  .operation-grid > * + * {
    border-left: 1px solid #d9e4e0;
  }
}

.service-list p {
  align-items: baseline;
  border-top: 1px solid #edf2f0;
  color: #607778;
  display: flex;
  font-size: 13px;
  font-weight: 700;
  justify-content: space-between;
  margin: 0;
  padding: 7px 0;
}

.service-list p:first-child {
  border-top: 0;
  padding-top: 0;
}

.service-list strong {
  color: #183b4b;
  margin-left: 12px;
  text-align: right;
}

.service-list span {
  display: block;
  font-size: 11px;
  font-weight: 700;
  margin-left: auto;
  text-align: right;
}

.data-list {
  margin: 0;
}

.data-list div {
  align-items: baseline;
  border-top: 1px solid #edf2f0;
  display: flex;
  font-size: 13px;
  font-weight: 700;
  justify-content: space-between;
  padding: 9px 0;
}

.data-list div:first-child {
  border-top: 0;
  padding-top: 0;
}

.data-list div:last-child {
  padding-bottom: 0;
}

.data-list dt {
  color: #607778;
  font-weight: 700;
}

.data-list dd {
  color: #183b4b;
  font-weight: 700;
  margin-left: 16px;
  text-align: right;
}

.payment-summary {
  background: #fff;
  border: 1px solid #d9e4e0;
  border-radius: 10px;
  color: #183b4b;
  height: 100%;
  overflow: hidden;
  padding: 20px;
}

.payment-summary-heading {
  align-items: center;
  background: #1976d2;
  color: #fff;
  display: flex;
  font-size: 13px;
  font-weight: 700;
  gap: 8px;
  margin: -20px -20px 22px;
  padding: 12px 16px;
}

.payment-summary-heading .v-icon {
  color: #fff;
}

.payment-total-label {
  color: #607778;
  font-size: 13px;
  font-weight: 700;
  margin: 0;
}

.payment-total {
  color: #183b4b;
  display: block;
  font-size: 34px;
  font-weight: 900;
  line-height: 1.2;
  margin: 6px 0 18px;
}

.payment-details {
  border-top: 1px solid #edf2f0;
  margin-bottom: 18px;
}

.payment-detail {
  align-items: center;
  border-bottom: 1px solid #edf2f0;
  display: flex;
  justify-content: space-between;
  padding: 9px 0;
}

.payment-detail > span {
  color: #607778;
  font-size: 13px;
  font-weight: 700;
}

.payment-detail b {
  color: #183b4b;
  font-size: 13px;
  font-weight: 800;
  margin-left: 16px;
  text-align: right;
}

.payment-status {
  background: #e1f0ed !important;
  color: #0b7285 !important;
  font-size: 11px;
  font-weight: 800;
  margin-left: 16px;
}

.payment-debt {
  align-items: center;
  background: #f3f6f5;
  border: 1px solid #d9e4e0;
  border-radius: 6px;
  display: flex;
  justify-content: space-between;
  margin: 0;
  padding: 14px;
}

.payment-debt span {
  color: #607778;
  font-size: 13px;
  font-weight: 700;
}

.payment-debt b {
  color: #183b4b;
  font-size: 16px;
  font-weight: 900;
}

.reprint-button {
  background: #1976d2 !important;
  border-color: #1976d2 !important;
  color: #fff !important;
  font-weight: 800;
  min-height: 44px;
  margin-top: 18px;
}

.reprint-button .v-icon {
  color: #fff !important;
}

.reprint-button:hover {
  background: #0d5cad !important;
  border-color: #0d5cad !important;
}

.reprint-button:focus::before {
  opacity: 0.2;
}

.tariff-item {
  border-top: 1px solid #edf2f0;
  color: #607778;
  font-size: 13px;
  font-weight: 700;
  padding: 12px 0;
}

.tariff-item:first-of-type {
  border-top: 0;
  padding-top: 0;
}

.tariff-item:last-child {
  padding-bottom: 0;
}

.tariff-item strong,
.tariff-item span {
  display: block;
}

.tariff-item strong {
  color: #183b4b;
}

.tariff-item span {
  color: #0b7285;
  font-size: 12px;
  font-weight: 700;
  margin-top: 3px;
}

.tariff-item p {
  margin: 6px 0 0;
}

@media (max-width: 599px) {
  .summary-heading,
  .section-heading {
    align-items: flex-start;
    flex-direction: column;
    gap: 10px;
  }

  .reference-block {
    text-align: left;
  }

  .data-list div,
  .service-list p {
    align-items: flex-start;
    flex-direction: column;
    gap: 3px;
  }

  .data-list dd,
  .service-list strong,
  .service-list span {
    margin-left: 0;
    text-align: left;
  }
}

@media print {
  .no-print {
    display: none !important;
  }

  .shipment-details {
    background: #fff;
    border: 0;
    margin: 0;
    max-width: none;
    padding: 0 !important;
    width: 100%;
  }

  .shipment-summary,
  .shipment-section {
    margin-bottom: 16px;
    overflow: visible;
  }

  .operation-panel,
  .shipment-card,
  .payment-record,
  .payment-summary,
  .tariff-panel {
    break-inside: avoid;
  }

  .summary-heading,
  .section-heading,
  .panel-title,
  .shipment-card-heading,
  .payment-summary-heading {
    break-after: avoid;
  }
}
</style>
